import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { connect } from "react-redux";
import { cloneDeep } from "lodash";
import { Avatar, Button, Modal, PageHeader, Table, Tooltip } from "antd";
import moment from "moment-jalaali";

import { CheckPermission, ListToTree } from "scripts/helpers";
import { setRoute } from "scripts/reducer";
import { Modals, Permissions } from "scripts/settings";
import API from "scripts/api";
import Resources from "scripts/resources";

import FormModal from "modals/CategoryForm";

const Component = ({ onSetRoute, routeName, parentName, permissions }) => {
	const initialState = {
		categories: [],
		data: [],
		images: [],
		videos: [],
		files: [],
	};
	const tableColumns = [
		{
			title: Resources.title,
			dataIndex: "title",
		},
		{
			title: Resources.status,
			dataIndex: "status",
			className: "center-cell color-cell",
			width: "100px",
			render: (value, record) =>
				permissions.status ? (
					<span
						className={`btn ${value ? "green" : "red"}`}
						onClick={() => handleStatus(record)}
					>
						{value ? Resources.active : Resources.inactive}
					</span>
				) : (
					<span className={value ? "green" : "red"}>
						{value ? Resources.active : Resources.inactive}
					</span>
				),
		},
		{
			title: `${Resources.date} ${Resources.record}`,
			dataIndex: "createdAt",
			className: "center-cell desc-cell ltr-cell",
			width: "100px",
			render: (value) => {
				const [date, time] = moment(value)
					.format(process.env.REACT_APP_DATE_TIME_FORMAT_JALALI)
					.split(" ");

				return (
					<>
						<span className="name">{date}</span>
						<small className="desc">{time}</small>
					</>
				);
			},
		},
		{
			title: Resources.action,
			dataIndex: "id",
			className: "center-cell",
			width: "120px",
			render: (value, record) => (
				<>
					{permissions.update && (
						<Tooltip title={Resources.edit}>
							<Button
								type="link"
								icon={<i className="fa fa-pencil-alt" />}
								onClick={() => handleForm(record)}
							/>
						</Tooltip>
					)}
					{permissions.delete && (
						<Tooltip title={Resources.delete}>
							<Button
								type="link"
								icon={<i className="fa fa-trash-alt" />}
								onClick={() => handleDelete(value)}
							/>
						</Tooltip>
					)}
				</>
			),
		},
	];
	const [state, setState] = useState(initialState);
	const { categories, data, loading, selected, files, images, videos } = state;
	const { pathname } = useLocation();
	const navigate = useNavigate();

	const fetchData = () => {
		setState((prevState) => ({ ...prevState, loading: true }));

		Promise.all([
			API.category.list({ paginate: false }),
			API.gallery.list({ paginate: false, section: "category" }),
			API.files.list({ paginate: false }),
			API.videos.list({ paginate: false }),
		])
			.then(
				([{ data }, { data: images }, { data: files }, { data: videos }]) => {
					setState((prevState) => ({
						...prevState,
						categories: ListToTree(cloneDeep(data), true),
						data: ListToTree(cloneDeep(data)),
						images: images.map(({ id, title }) => ({
							label: title,
							value: id,
						})),
						files: files.map(({ id, title }) => ({ label: title, value: id })),
						videos: videos.map(({ id, title }) => ({
							label: title,
							value: id,
						})),

						loading: false,
					}));
					console.log(ListToTree(cloneDeep(data)));
				}
			)
			.catch(() => {
				setState((prevState) => ({ ...prevState, loading: false }));
			});
	};

	const handleForm = (selected, reload) => {
		if (reload) {
			fetchData();
		} else {
			setState((prevState) => ({ ...prevState, selected }));

			navigate(`${pathname}#${selected ? Modals.EDIT : Modals.CREATE}`);
		}
	};

	const handleStatus = (selected) => {
		Modal.confirm({
			title: Resources.statusConfirm,
			okText: Resources.yes,
			okType: "danger",
			cancelText: Resources.no,
			onOk: () => {
				setState((prevState) => ({ ...prevState, loading: true }));

				API.category
					.status(selected.id)
					.then(() => {
						setState((prevState) => ({ ...prevState, loading: false }));

						fetchData();
					})
					.catch(() => {
						setState((prevState) => ({ ...prevState, loading: false }));
					});
			},
		});
	};

	const handleDelete = (id) => {
		Modal.confirm({
			title: Resources.deleteConfirm,
			okText: Resources.yes,
			okType: "danger",
			cancelText: Resources.no,
			onOk: () => {
				setState((prevState) => ({ ...prevState, loading: true }));

				API.category
					.delete(id)
					.then(() => {
						setState((prevState) => ({ ...prevState, loading: false }));

						fetchData();
					})
					.catch(() => {
						setState((prevState) => ({ ...prevState, loading: false }));
					});
			},
		});
	};

	const componentDidMount = () => {
		onSetRoute({ openKey: parentName, selectedKey: routeName });

		fetchData();
	};

	useEffect(componentDidMount, []);

	return (
		<>
			<PageHeader
				title={Resources.categories}
				extra={
					permissions.create && (
						<Button
							type="primary"
							onClick={() => handleForm()}
							loading={loading}
						>
							{Resources.add}
						</Button>
					)
				}
			/>
			<Table
				columns={tableColumns}
				dataSource={data}
				loading={loading}
				pagination={false}
				scroll={{ x: 930 }}
				rowKey="id"
			/>
			<FormModal
				data={selected}
				onUpdate={handleForm}
				options={{ categories, images, videos, files }}
			/>
		</>
	);
};

export default connect(
	(state) => {
		return {
			permissions: {
				create: CheckPermission(
					state.user.permissions,
					Permissions.CATEGORY_CREATE
				),
				delete: CheckPermission(
					state.user.permissions,
					Permissions.CATEGORY_DELETE
				),
				update: CheckPermission(
					state.user.permissions,
					Permissions.CATEGORY_UPDATE
				),
				status: CheckPermission(
					state.user.permissions,
					Permissions.CATEGORY_STATUS
				),
			},
		};
	},
	(dispatch) => {
		return {
			onSetRoute: (data) => dispatch(setRoute(data)),
		};
	}
)(Component);
